import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TableIconButton,
  TableIconLink,
} from '../../components/TableIconAction';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, SortState } from '../../components/SortableTh';
import {
  Badge,
  BanIcon,
  Button,
  Callout,
  CheckCircleIcon,
  ClockIcon,
  DateInput,
  DocumentTextIcon,
  EyeIcon,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  CoinIcon,
  SearchIcon,
  SegmentedControl,
  Select,
  TableIcon,
  TextInput,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { ExportIconButtons } from '../../components/ExportIconButtons';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { InvoiceCategory, InvoiceListItem } from '../../types/invoice';
import { formatDate, formatMoney } from '../../utils/format';
import {
  collectPages,
  exportTabularList,
  moneyCell,
  sumMoney,
  textCell,
  type ListExportFormat,
} from '../../utils/exportTabularList';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SERVER_SORT_KEYS = new Set([
  'invoiceDate',
  'invoiceNo',
  'dueDate',
  'customer',
]);

type InvoiceStatusFilter = 'all' | 'unpaid' | 'paid' | 'overdue';
const PILL_ICON_CLASS = 'h-3.5 w-3.5';

function invoiceStatusPills() {
  const filters = t().invoices.filters;
  return [
    {
      id: 'all' as const,
      label: filters.all,
      icon: <TableIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'unpaid' as const,
      label: filters.unpaid,
      icon: <DocumentTextIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'paid' as const,
      label: filters.paid,
      icon: <CheckCircleIcon className={PILL_ICON_CLASS} />,
    },
    {
      id: 'overdue' as const,
      label: filters.overdue,
      icon: <ClockIcon className={PILL_ICON_CLASS} />,
    },
  ];
}

const invoiceSortGetters: Record<
  string,
  (item: InvoiceListItem) => string | number | boolean | null | undefined
> = {
  invoiceDate: (i) => i.invoiceDate,
  invoiceNo: (i) => i.invoiceNo,
  customer: (i) => i.customer.name,
  category: (i) => i.category?.name,
  dueDate: (i) => i.dueDate,
  chargesInvoice: (i) => i.charges.invoice,
  chargesCash: (i) => i.charges.cash,
  chargesTotal: (i) => i.charges.total,
  paid: (i) => i.paid.total,
  outstanding: (i) => i.outstanding.total,
  status: (i) => i.status,
};

export function InvoiceListPage() {
  const labels = t().invoices;
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [categories, setCategories] = useState<InvoiceCategory[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    pageCount: 1,
    totals: {
      chargesInvoice: '0.00',
      chargesCash: '0.00',
      chargesTotal: '0.00',
      paid: '0.00',
      outstanding: '0.00',
    },
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') ?? '',
  );

  const search = searchParams.get('search') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const categoryId = searchParams.get('categoryId') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const statusFilter: InvoiceStatusFilter =
    statusParam === 'unpaid' ||
    statusParam === 'paid' ||
    statusParam === 'overdue'
      ? statusParam
      : 'all';
  const status = statusFilter === 'all' ? '' : statusFilter;
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize =
    Number(searchParams.get('pageSize') ?? '20') || 20;
  const sortBy = searchParams.get('sortBy') ?? 'invoiceDate';
  const sortDir =
    searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const sort: SortState = { key: sortBy, direction: sortDir };

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    void Promise.all([
      api.getCustomerLookup({ active: 'true' }),
      api.getInvoiceCategories(false),
    ]).then(([customerResult, categoryResult]) => {
      setCustomers(customerResult);
      setCategories(categoryResult);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const serverSortBy = SERVER_SORT_KEYS.has(sortBy)
          ? sortBy
          : 'invoiceDate';
        const result = await api.getInvoices({
          search: search || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          status: status || undefined,
          page,
          pageSize,
          sortBy: serverSortBy,
          sortDir: SERVER_SORT_KEYS.has(sortBy) ? sortDir : 'desc',
        });
        if (!cancelled) {
          setItems(result.data);
          setMeta(result.meta);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    search,
    customerId,
    categoryId,
    dateFrom,
    dateTo,
    status,
    page,
    pageSize,
    sortBy,
    sortDir,
  ]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }

  function toggleSort(key: string) {
    const next = new URLSearchParams(searchParams);
    if (sort.key === key) {
      next.set('sortDir', sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sortBy', key);
      next.set('sortDir', 'asc');
    }
    next.delete('page');
    setSearchParams(next);
  }

  const displayItems = useMemo(() => {
    if (SERVER_SORT_KEYS.has(sort.key)) return items;
    return sortItems(items, sort, invoiceSortGetters);
  }, [items, sort]);

  const hasFilters = Boolean(
    search || customerId || categoryId || dateFrom || dateTo || status,
  );

  function clearFilters() {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  }

  async function handleExport(format: ListExportFormat) {
    setExporting(true);
    setError(null);
    try {
      const serverSort = SERVER_SORT_KEYS.has(sort.key);
      const rows = await collectPages((nextPage) =>
        api.getInvoices({
          search: search || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          status: status || undefined,
          page: nextPage,
          pageSize: 100,
          sortBy: serverSort ? sort.key : 'invoiceDate',
          sortDir: serverSort ? sort.direction : 'desc',
        }),
      );
      const exported = serverSort
        ? rows
        : sortItems(rows, sort, invoiceSortGetters);
      const statusLabels = t().invoices.statuses;
      const filterLabel =
        statusFilter === 'unpaid'
          ? labels.filters.unpaid
          : statusFilter === 'paid'
            ? labels.filters.paid
            : statusFilter === 'overdue'
              ? labels.filters.overdue
              : labels.filters.all;
      exportTabularList({
        filename: 'timologia.xls',
        title: t().nav.invoices,
        meta: [
          { label: t().common.search, value: search || '—' },
          {
            label: labels.customer,
            value:
              customers.find((customer) => String(customer.id) === customerId)
                ?.name ?? labels.filters.all,
          },
          {
            label: labels.category,
            value:
              categories.find((category) => String(category.id) === categoryId)
                ?.name ?? labels.filters.all,
          },
          { label: labels.filters.dateFrom, value: dateFrom || '—' },
          { label: labels.filters.dateTo, value: dateTo || '—' },
          { label: labels.status, value: filterLabel },
        ],
        headers: [
          labels.invoiceDate,
          labels.invoiceNo,
          labels.customer,
          labels.category,
          labels.dueDate,
          t().financial.invoice,
          t().financial.cash,
          t().financial.grandTotal,
          labels.paid,
          labels.outstanding,
          labels.status,
        ],
        columnWidths: [14, 16, 28, 18, 14, 14, 14, 14, 14, 14, 16],
        numericIndexes: [5, 6, 7, 8, 9],
        rows: exported.map((invoice) => [
          textCell(formatDate(invoice.invoiceDate)),
          textCell(invoice.invoiceNo),
          textCell(invoice.customer.name),
          textCell(invoice.category?.name),
          textCell(formatDate(invoice.dueDate)),
          moneyCell(invoice.charges.invoice),
          moneyCell(invoice.charges.cash),
          moneyCell(invoice.charges.total),
          moneyCell(invoice.paid.total),
          moneyCell(invoice.outstanding.total),
          textCell(statusLabels[invoice.status]),
        ]),
        pdfRows: exported.map((invoice) => [
          formatDate(invoice.invoiceDate),
          invoice.invoiceNo,
          invoice.customer.name,
          invoice.category?.name || '—',
          formatDate(invoice.dueDate),
          formatMoney(invoice.charges.invoice),
          formatMoney(invoice.charges.cash),
          formatMoney(invoice.charges.total),
          formatMoney(invoice.paid.total),
          formatMoney(invoice.outstanding.total),
          statusLabels[invoice.status],
        ]),
        moneyTotals: [
          sumMoney(exported.map((invoice) => invoice.charges.invoice)),
          sumMoney(exported.map((invoice) => invoice.charges.cash)),
          sumMoney(exported.map((invoice) => invoice.charges.total)),
          sumMoney(exported.map((invoice) => invoice.paid.total)),
          sumMoney(exported.map((invoice) => invoice.outstanding.total)),
        ],
        format,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete(id: number) {
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteInvoice(id);
      const removed = items.find((item) => item.id === id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (removed) {
        setMeta((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
          totals: {
            chargesInvoice: sumMoney([
              prev.totals.chargesInvoice,
              `-${removed.charges.invoice}`,
            ]),
            chargesCash: sumMoney([
              prev.totals.chargesCash,
              `-${removed.charges.cash}`,
            ]),
            chargesTotal: sumMoney([
              prev.totals.chargesTotal,
              `-${removed.charges.total}`,
            ]),
            paid: sumMoney([prev.totals.paid, `-${removed.paid.total}`]),
            outstanding: sumMoney([
              prev.totals.outstanding,
              `-${removed.outstanding.total}`,
            ]),
          },
        }));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            <ExportIconButtons
              disabled={loading || exporting || meta.total === 0}
              onExcel={() => void handleExport('excel')}
              onPdf={() => void handleExport('pdf')}
            />
            {hasFilters ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <XIcon className="h-3.5 w-3.5" />
                {t().common.clearFilters}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updateParam('search', searchInput)}
            >
              <SearchIcon className="h-3.5 w-3.5" />
              {t().common.search}
            </Button>
            <Button type="button" size="sm" onClick={() => navigate('/invoices/new')}>
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.new}
            </Button>
          </>
        }
      >
        <FilterField wide label={t().common.search}>
          <TextInput
            type="search"
            value={searchInput}
            showClearButton={Boolean(searchInput)}
            size="sm"
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateParam('search', searchInput);
              }
            }}
          />
        </FilterField>
        <FilterField label={labels.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.customer}
            options={customers.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => updateParam('customerId', e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.category}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.category}
            options={categories.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => updateParam('categoryId', e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.filters.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.filters.dateFrom}
            onChange={(e) => updateParam('dateFrom', e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.filters.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.filters.dateTo}
            onChange={(e) => updateParam('dateTo', e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2">
        <SegmentedControl
          value={statusFilter}
          onChange={(next) =>
            updateParam('status', next === 'all' ? '' : next)
          }
          size="sm"
          aria-label={labels.status}
          options={invoiceStatusPills()}
        />
      </div>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel
        footer={
          !loading && meta.total > 0 ? (
            <ListPagination
              page={meta.page}
              totalPages={meta.pageCount}
              total={meta.total}
              itemsPerPage={meta.pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(nextPage) =>
                updateParam('page', String(nextPage))
              }
              onItemsPerPageChange={(size) =>
                updateParam('pageSize', String(size))
              }
            />
          ) : null
        }
      >
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh
                  sortKey="invoiceDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.invoiceDate}
                </SortableTh>
                <SortableTh
                  sortKey="invoiceNo"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.invoiceNo}
                </SortableTh>
                <SortableTh sortKey="customer" sort={sort} onSort={toggleSort}>
                  {labels.customer}
                </SortableTh>
                <SortableTh sortKey="category" sort={sort} onSort={toggleSort}>
                  {labels.category}
                </SortableTh>
                <SortableTh sortKey="dueDate" sort={sort} onSort={toggleSort}>
                  {labels.dueDate}
                </SortableTh>
                <SortableTh
                  sortKey="chargesInvoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.invoice}
                </SortableTh>
                <SortableTh
                  sortKey="chargesCash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.cash}
                </SortableTh>
                <SortableTh
                  sortKey="chargesTotal"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.grandTotal}
                </SortableTh>
                <SortableTh
                  sortKey="paid"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.paid}
                </SortableTh>
                <SortableTh
                  sortKey="outstanding"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.outstanding}
                </SortableTh>
                <SortableTh sortKey="status" sort={sort} onSort={toggleSort}>
                  {labels.status}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((invoice) => (
                <tr key={invoice.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconLink
                        to={`/invoices/${invoice.id}`}
                        label={labels.view}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </TableIconLink>
                      <TableIconLink
                        to={`/invoices/${invoice.id}/edit`}
                        label={labels.editAction}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </TableIconLink>
                      {invoice.status !== 'PAID' && !invoice.isDeleted ? (
                        <TableIconLink
                          to={`/payments/new?customerId=${invoice.customer.id}&invoiceId=${invoice.id}`}
                          label={labels.payAction}
                        >
                          <CoinIcon className="h-4 w-4" />
                        </TableIconLink>
                      ) : null}
                      {isAdmin ? (
                        <TableIconButton
                          label={labels.softDelete}
                          variant="danger"
                          onClick={() => void handleDelete(invoice.id)}
                        >
                          <BanIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to={`/invoices/${invoice.id}`}
                      className="text-brand hover:underline"
                    >
                      {invoice.invoiceNo}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{invoice.customer.name}</td>
                  <td className="px-3 py-2">
                    {invoice.category?.name || '—'}
                  </td>
                  <td className="px-3 py-2">{formatDate(invoice.dueDate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(invoice.charges.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(invoice.charges.cash)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(invoice.charges.total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(invoice.paid.total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(invoice.outstanding.total)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={invoice.status} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={dataTableFootClass}>
              <tr>
                <td className="px-3 py-2" colSpan={6}>
                  {`${t().common.total} · ${(meta.total === 1
                    ? labels.listInvoiceCountOne
                    : labels.listInvoiceCount
                  ).replace('{count}', String(meta.total))}`}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.chargesInvoice)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.chargesCash)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.chargesTotal)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.paid)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.outstanding)}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}

function StatusBadge({
  status,
}: {
  status: InvoiceListItem['status'];
}) {
  const label = t().invoices.statuses[status];
  const variant =
    status === 'PAID'
      ? 'success'
      : status === 'OVERDUE'
        ? 'danger'
        : status === 'PARTIALLY_PAID'
          ? 'warning'
          : 'secondary';

  return <Badge variant={variant}>{label}</Badge>;
}
